import { Button } from '@design/components/button/Button';
import { isJapaneseLocale } from '@helpers/utils/locale/locale';
import { ChatInput } from '@terminal/components/chat';
import { TrackingMetadata } from '@terminal/services/tracking/posthog/constants';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useChatNavigation } from '@terminal/views/alfred/use-chat-navigation/useChatNavigation';
import clsx from 'clsx';
import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './AlfredFooter.module.scss';
import { useCollapsibleFooter } from './useCollapsibleFooter';

export interface AlfredFooterProps {
  renderChatInput?: (override?: { cta?: ReactNode }) => ReactNode;
  selectedBadges?: ReactNode[];
  ctaLabel?: string;
  onCtaClick?: () => void;
  isCtaLoading?: boolean;
  shouldHide?: boolean;
  trackingMetadata?: TrackingMetadata;
  fixed?: boolean;
  autoFocus?: boolean;
}

export const AlfredFooter = ({
  shouldHide = false,
  trackingMetadata,
  fixed = false,
  autoFocus = false,
}: AlfredFooterProps) => {
  const { t } = useTranslation(['common']);
  const { mode, setMode, resetPreviousMode } = useChatNavigation();
  const isCollapsible = useFlags(state =>
    state.getFeatureFlagState('REASON-2677-collapsible-alfred-footer')
  );
  const isJapanese = isJapaneseLocale();

  const {
    containerRef,
    isExpanded,
    isSettled,
    containerHandlers,
    triggerHandlers,
  } = useCollapsibleFooter({
    isCollapsible,
    initiallyFocused: autoFocus && !shouldHide,
  });

  useEffect(() => {
    if (mode !== 'research') {
      setMode('research');
    }

    resetPreviousMode();
  }, []);

  return (
    <footer
      className={clsx(styles.alfredFooterRoot, {
        [styles.hidden]: shouldHide,
        [styles.fixed]: fixed,
        [styles.sticky]: !fixed,
      })}
    >
      <div className={styles.inputRegion}>
        <div
          className={clsx(styles.collapsible, {
            [styles.expanded]: isExpanded,
            [styles.settled]: isExpanded && isSettled,
            [styles.isJapanese]: isJapanese,
          })}
          data-testid="alfred-footer-collapsible"
          ref={containerRef}
          {...containerHandlers}
        >
          {isCollapsible && (
            <Button
              className={styles.trigger}
              data-testid="alfred-footer-expand-trigger"
              label={t('common:sideNavigation.alfred')}
              iconName="Alfred"
              variant="empty"
              size="large"
              aria-hidden={isExpanded}
              tabIndex={isExpanded ? -1 : 0}
              {...triggerHandlers}
            />
          )}
          <div className={styles.panel}>
            <div className={styles.panelInner}>
              <ChatInput
                config={{
                  autoFocus: autoFocus && !shouldHide,
                  alfredEverywhere: true,
                  allowedModes: ['research'],
                }}
                trackingMetadata={trackingMetadata}
              />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
